import { BOGGLE_CHALLENGE_VOWELS } from '~/boggle/config/challengeModLetterConstants'
import type { BoggleChallengeResolvedEffect } from '~/boggle/config/challenges/modTypes'
import type { LetterWeight } from '~/boggle/engine/core'

export function applyBoggleChallengeResolvedEffectToLetterWeights(
  weights: ReadonlyArray<LetterWeight>,
  effect: Extract<BoggleChallengeResolvedEffect, { kind: 'letter-weights' }>,
) {
  if (effect.transform.kind === 'restrict-letter-set') {
    const allowedLetters = new Set(
      effect.transform.letters.map(letter => letter.toUpperCase()),
    )

    return weights.map(([letter, weight]) =>
      allowedLetters.has(letter) ? ([letter, weight] as const) : ([letter, 0] as const),
    )
  }

  if (effect.transform.kind === 'multiply-letter-weight') {
    const boostedLetter = effect.transform.letter.toUpperCase()
    const multiplier = Number.isFinite(effect.transform.multiplier)
      ? Math.max(1, effect.transform.multiplier)
      : 1

    return weights.map(([letter, weight]) =>
      letter === boostedLetter
        ? ([letter, weight * multiplier] as const)
        : ([letter, weight] as const),
    )
  }

  if (effect.transform.kind === 'remove-letter') {
    const blockedLetter = effect.transform.letter.toUpperCase()

    return weights.map(([letter, weight]) =>
      letter === blockedLetter ? ([letter, 0] as const) : ([letter, weight] as const),
    )
  }

  const replacement = effect.transform.replacement.toUpperCase()
  const totalVowelWeight = weights.reduce(
    (sum, [letter, weight]) => sum + (BOGGLE_CHALLENGE_VOWELS.has(letter) ? weight : 0),
    0,
  )
  let sawReplacement = false

  const nextWeights = weights.map(([letter, weight]) => {
    if (letter === replacement) {
      sawReplacement = true
      return [letter, totalVowelWeight] as const
    }

    if (BOGGLE_CHALLENGE_VOWELS.has(letter)) {
      return [letter, 0] as const
    }

    return [letter, weight] as const
  })

  if (sawReplacement) {
    return nextWeights
  }

  return [...nextWeights, [replacement, totalVowelWeight] as const]
}
